import React from 'react';
import { format } from 'date-fns';
import { Task, PRIORITY_COLORS } from '../types/Task';
import { Edit, Trash2, ExternalLink, Clock, User, Calendar, Hash, Play, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { KanbanColumn } from '../services/api';
import './TaskList.css';

interface TaskListProps {
  tasks: Task[];
  columns: KanbanColumn[];
  onEdit: (task: Task) => void;
  onDelete: (id: string) => void;
  onImagePreview?: (task: Task) => void;
}

const TaskList: React.FC<TaskListProps> = ({ tasks, columns, onEdit, onDelete, onImagePreview }) => {
  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00Z') return '';
    try {
      return format(new Date(dateString), 'MMM dd');
    } catch {
      return '';
    }
  };

  const isOverdue = (task: Task) => {
    // Overdue gdy:
    // 1. Jest expectedDate 
    // 2. expectedDate jest w przeszłości
    // 3. I nie ma workCompletedAt LUB workCompletedAt jest późniejsze niż expectedDate
    if (!task.expectedDate) return false;
    
    const expectedDate = new Date(task.expectedDate);
    const now = new Date();
    
    // Jeśli expectedDate nie został przekroczony, nie jest overdue
    if (expectedDate >= now) return false;
    
    // Jeśli nie ma workCompletedAt, jest overdue
    if (!task.workCompletedAt) return true;
    
    // Jeśli workCompletedAt jest późniejsze niż expectedDate, jest overdue
    const workCompletedDate = new Date(task.workCompletedAt);
    return workCompletedDate > expectedDate;
  };

  const getProgressPercentage = (task: Task) => {
    if (task.originalEstimate === 0) return 0;
    return Math.min((task.actualTime / task.originalEstimate) * 100, 100);
  };

  // Create a mapping from status to column color
  const getStatusColor = (status: string): string => {
    const column = columns.find(col => col.status === status);
    return column?.color || '#646669'; // Default fallback color
  };

  // Get the display name for status from column title
  const getStatusDisplayName = (status: string): string => {
    const column = columns.find(col => col.status === status);
    return column?.title || status.replace('-', ' ');
  };

  const handleImageClick = (e: React.MouseEvent, task: Task) => {
    e.stopPropagation(); // Prevent triggering other actions
    if (task.images && task.images.length > 0 && onImagePreview) {
      onImagePreview(task);
    }
  };

  if (tasks.length === 0) {
    return (
      <div className="task-list-empty">
        <h2>No tasks found</h2>
        <p>Create your first task to get started</p>
      </div>
    );
  }

  return (
    <div className="task-list">
      <div className="task-list-header">
        <div className="task-list-header-cell id">ID</div>
        <div className="task-list-header-cell summary">Task</div>
        <div className="task-list-header-cell priority">Priority</div>
        <div className="task-list-header-cell status">Status</div>
        <div className="task-list-header-cell owner">Owner</div>
        <div className="task-list-header-cell due-date">Due Date</div>
        <div className="task-list-header-cell work-dates">Work Dates</div>
        <div className="task-list-header-cell progress">Progress</div>
        <div className="task-list-header-cell actions">Actions</div>
      </div>
      
      <div className="task-list-body">
        {tasks.map((task) => (
          <div 
            key={task.id} 
            className={`task-list-row ${task.status} ${isOverdue(task) ? 'overdue' : ''}`}
          >
            <div className="task-list-cell id">
              <div className="task-id" title={`Task ID: ${task.id}`}>
                <Hash size={12} />
                {task.id.substring(0, 8)}
              </div>
            </div>

            <div className="task-list-cell summary">
              <div className="task-summary-content">
                <h4 className="task-title">{task.summary}</h4>
                {task.description && (
                  <p className="task-description">{task.description}</p>
                )}
                {task.labels.length > 0 && (
                  <div className="task-labels">
                    {task.labels.slice(0, 3).map((label, index) => (
                      <span key={index} className="label">
                        {label}
                      </span>
                    ))}
                    {task.labels.length > 3 && (
                      <span className="label-more">+{task.labels.length - 3}</span>
                    )}
                  </div>
                )}
                {task.videoTutorialLink && (
                  <div className="video-tutorial">
                    <a 
                      href={task.videoTutorialLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="tutorial-link"
                    >
                      <ExternalLink size={12} />
                      <span>{task.videoTutorialTitle || 'Video'}</span>
                    </a>
                  </div>
                )}
                {task.images && task.images.length > 0 && (
                  <div className="image-preview" onClick={(e) => handleImageClick(e, task)}>
                    <ImageIcon size={12} />
                    <span>{task.images.length} images</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="task-list-cell priority">
              <span 
                className="priority-badge" 
                style={{ backgroundColor: PRIORITY_COLORS[task.priority] }}
              >
                {task.priority}
              </span>
            </div>
            
            <div className="task-list-cell status">
              <span 
                className="status-badge"
                style={{ backgroundColor: getStatusColor(task.status) }}
              >
                {getStatusDisplayName(task.status)}
              </span>
            </div>
            
            <div className="task-list-cell owner">
              <div className="owner-info">
                <User size={14} />
                <span>{task.owner || 'Unassigned'}</span>
              </div>
            </div>
            
            <div className="task-list-cell due-date">
              {task.expectedDate && formatDate(task.expectedDate) && (
                <div className={`date-info ${isOverdue(task) ? 'overdue-text' : ''}`}>
                  <Calendar size={14} />
                  <span>{formatDate(task.expectedDate)}</span>
                </div>
              )}
            </div>

            <div className="task-list-cell work-dates">
              <div className="work-dates-info">
                {task.workStartedAt && formatDate(task.workStartedAt) && (
                  <div className="work-date started">
                    <Play size={12} />
                    <span>{formatDate(task.workStartedAt)}</span>
                  </div>
                )}
                {task.workCompletedAt && formatDate(task.workCompletedAt) && (
                  <div className="work-date completed">
                    <CheckCircle size={12} />
                    <span>{formatDate(task.workCompletedAt)}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="task-list-cell progress">
              {task.originalEstimate > 0 && (
                <div className="progress-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${getProgressPercentage(task)}%`,
                        backgroundColor: getProgressPercentage(task) > 100 ? 
                          'var(--error-color)' : 'var(--main-color)'
                      }}
                    />
                  </div>
                  <div className="time-info">
                    <Clock size={12} />
                    <span>{task.actualTime}h / {task.originalEstimate}h</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="task-list-cell actions">
              <div className="task-actions">
                <button 
                  className="action-btn edit-btn" 
                  onClick={() => onEdit(task)}
                  title="Edit task"
                >
                  <Edit size={14} />
                </button>
                <button 
                  className="action-btn delete-btn" 
                  onClick={() => onDelete(task.id)}
                  title="Delete task"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TaskList; 